import React from 'react'
import { Button } from '@/ui/components/atoms/Button'
import { DomicileAddress } from '../../types/preferences.types'
import './InformationSection.styles.css'

interface InformationSectionProps {
  data: {
    domicileAddresses: DomicileAddress[]
    phoneNumber: string
    email: string
    emergencyContact: string
  }
  onChange: (data: any) => void
}

export const InformationSection: React.FC<InformationSectionProps> = ({ data, onChange }) => {
  const handleAddDomicile = () => {
    const newId = Math.max(...data.domicileAddresses.map(d => d.id)) + 1
    onChange({
      ...data,
      domicileAddresses: [
        ...data.domicileAddresses,
        { id: newId, name: `Domicile ${newId}`, street: '', city: '', postalCode: '' }
      ]
    })
  }

  const handleRemoveDomicile = (id: number) => {
    if (data.domicileAddresses.length <= 1) return
    onChange({
      ...data,
      domicileAddresses: data.domicileAddresses.filter(d => d.id !== id)
    })
  }

  const handleDomicileChange = (id: number, field: keyof DomicileAddress, value: string) => {
    onChange({
      ...data,
      domicileAddresses: data.domicileAddresses.map(d =>
        d.id === id ? { ...d, [field]: value } : d
      )
    })
  }

  const handleFieldChange = (field: string, value: string) => {
    onChange({
      ...data,
      [field]: value
    })
  }

  return (
    <div className="information-section">
      <div className="section-header">
        <h2 className="section-title">Informations perso</h2>
        <p className="section-description">
          Gérez vos informations de contact et vos adresses de domicile
        </p>
      </div>

      <div className="contact-info-card">
        <h3 className="card-title">
          <span className="card-icon">📞</span>
          Informations de contact
        </h3>
        <div className="contact-grid">
          <div className="form-group">
            <label className="form-label">Numéro de téléphone</label>
            <div className="input-wrapper">
              <span className="input-icon">📱</span>
              <input
                type="tel"
                className="form-input with-icon"
                value={data.phoneNumber}
                onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
                placeholder="+32 XXX XX XX XX"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Adresse email</label>
            <div className="input-wrapper">
              <span className="input-icon">✉️</span>
              <input
                type="email"
                className="form-input with-icon"
                value={data.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="votre.email@example.com"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Contact d'urgence</label>
            <div className="input-wrapper">
              <span className="input-icon">🚨</span>
              <input
                type="tel"
                className="form-input with-icon"
                value={data.emergencyContact}
                onChange={(e) => handleFieldChange('emergencyContact', e.target.value)}
                placeholder="+32 XXX XX XX XX"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="domiciles-section">
        <div className="section-header-with-action">
          <div>
            <h3 className="card-title">
              <span className="card-icon">🏠</span>
              Adresses de domicile
            </h3>
            <p className="card-subtitle">Gérez vos différents points de départ pour les expertises</p>
          </div>
          <Button
            variant="secondary"
            size="small"
            onClick={handleAddDomicile}
            className="add-domicile-btn"
          >
            <span style={{ marginRight: '6px' }}>➕</span>
            Ajouter une adresse
          </Button>
        </div>

        <div className="domiciles-grid">
          {data.domicileAddresses.map((domicile, index) => (
            <div key={domicile.id} className="domicile-card">
              <div className="domicile-header">
                <div className="domicile-number">{index + 1}</div>
                <input
                  type="text"
                  className="domicile-name"
                  value={domicile.name}
                  onChange={(e) => handleDomicileChange(domicile.id, 'name', e.target.value)}
                  placeholder="Nom du domicile"
                />
                {data.domicileAddresses.length > 1 && (
                  <button
                    className="domicile-delete-btn"
                    onClick={() => handleRemoveDomicile(domicile.id)}
                    title="Supprimer cette adresse"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="domicile-fields">
                <div className="form-group">
                  <label className="mini-label">Rue et numéro</label>
                  <input
                    type="text"
                    className="form-input compact"
                    value={domicile.street}
                    onChange={(e) => handleDomicileChange(domicile.id, 'street', e.target.value)}
                    placeholder="Ex: Rue de la Loi 42"
                  />
                </div>
                <div className="domicile-location-row">
                  <div className="form-group" style={{ width: '35%' }}>
                    <label className="mini-label">Code postal</label>
                    <input
                      type="text"
                      className="form-input compact"
                      value={domicile.postalCode}
                      onChange={(e) => handleDomicileChange(domicile.id, 'postalCode', e.target.value)}
                      placeholder="1000"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="mini-label">Ville</label>
                    <input
                      type="text"
                      className="form-input compact"
                      value={domicile.city}
                      onChange={(e) => handleDomicileChange(domicile.id, 'city', e.target.value)}
                      placeholder="Bruxelles"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}